import { useQuery } from "@tanstack/react-query";
import { useStore } from "zustand";
import { useChatStore } from "@/components/Chat/store";
import { backendService } from "@/lib/backend";
import { usePerfectDayMutationStream } from "@/lib/query/functions/perfect-day/hooks/useMutationStream";
import { queryOptions_perfectDayScenes } from "@/lib/query/functions/perfect-day/scenes";

export function usePerfectDayChatStore(sceneId: string) {
	const chatStore = useChatStore();
	const chat = useStore(chatStore);

	const queryPerfectDay = useQuery({ ...queryOptions_perfectDayScenes(), retry: false });

	const findSceneById = (scenes: typeof queryPerfectDay.data, id: string) =>
		scenes?.find((scene) => scene.id === id);

	const currentScene = findSceneById(queryPerfectDay.data, sceneId);

	const [streamedResponse, mutationStream, streamStatus] = usePerfectDayMutationStream({
		mutationKey: ["perfectDay", "generate"],
		mutationFn: (params) =>
			backendService.perfectDay.perfectDayControllerGenerateChatResponse(
				currentScene?.chat.id || "",
				params,
			),
		onSettled: () => {
			queryPerfectDay.refetch().then((result) => {
				if (!result.data) return;

				const previousStatus = currentScene?.chat.status;
				const newScene = findSceneById(result.data, sceneId);
				const newStatus = newScene?.chat.status;

				if (previousStatus !== "IMAGE_GENERATION" && newStatus === "IMAGE_GENERATION") {
					chat.setStreamUserMessage("");
					mutationStream.mutate();
					return;
				}

				chat.setResponding(false);
			});
		},
	});

	if (streamedResponse !== chat.streamAssistantMessage && streamStatus !== "GENERATING_IMAGE") {
		chat.setStreamAssistantMessage(streamedResponse);
	}

	return {
		queryPerfectDay,
		streamStatus,
		chat,
		chatStore,
		mutationStream,
		perfectDaySceneWithChat: currentScene,
		perfectDayScenes: queryPerfectDay.data,
	};
}
